// DA-15980 — шторка сравнения источников слоя «Землепользование».
//
// ПОЧЕМУ ВТОРАЯ КАРТА, А НЕ КЛИП ОДНОГО СЛОЯ. В Mapbox GL JS нет обрезки отдельного слоя по
// экрану: ни маски, ни screen-space clip. Выражение within не годится даже теоретически — оно
// поддерживает только точки и линии, а у нас заливки. Поэтому правая половина — отдельный
// инстанс карты поверх основного, обрезанный clip-path.
//
// ПОЧЕМУ НЕ mapbox-gl-compare. Плагин живой и с нашей версией mapbox-gl совместим (его пример в
// документации работает на 3.x), дело не в возрасте. Он клипает контейнеры свойством
// `clip: rect(...)`, а оно режет только отрисовку, не хит-тест: верхняя карта продолжала бы
// ловить клики и на левой половине, где видна нижняя, и попап по полигону слева отдавал бы
// данные не того слоя. Для сравнения источников это ровно та ошибка, которую нельзя допускать.
// clip-path режет и события — поэтому клипаем им и рисуем свою ручку.
//
// СИНХРОНИЗАЦИЯ СВОЯ, а не @mapbox/mapbox-gl-sync-move: тот заявляет peer mapbox-gl <2.0.0
// (у нас 2.15) и не умеет отписываться, а отписка нужна при выключении шторки.
//
// СТИЛЬ ЗЕРКАЛИТСЯ ЦЕЛИКОМ С ОСНОВНОЙ КАРТЫ, а не собирается здесь из констант. Раньше вторая
// карта держала голую подложку и один слой типологии — из-за чего справа пропадали все
// остальные включённые слои, а подложка вдобавок стояла на дефолтном языке. Теперь на каждое
// styledata основной карты берём её getStyle() и накладываем ровно две правки: выкидываем
// старое землепользование и делаем видимым новое. Всё прочее — подложка, включённые слои, их
// фильтры и раскраска — приезжает даром и остаётся в актуальном состоянии.
// Исключение — слои deck.gl: они живут в собственном канвасе поверх карты (MapDeck), в стиль
// mapbox не попадают и справа не отображаются.
//
// ЯЗЫК ПОДЛОЖКИ НАСТРАИВАЕТСЯ ОТДЕЛЬНО. setLanguage в mapbox-gl 2.x — свойство карты, а не
// части стиля: оно перезагружает источники с нужным языком и в getStyle() не попадает. Поэтому
// зеркалирование стиля его не переносит, и язык задаётся конструктором и setLanguage явно.
//
// МОНТИРУЕТСЯ ВМЕСТЕ СО СЛОЕМ, А НЕ С ТУМБЛЕРОМ. Пока active=false, оверлей полностью срезан
// clip-path, но карта живёт и грузит тайлы — включение шторки получается без ожидания. Кэш
// тайлов у второй карты свой: на HTTP-кэш браузера рассчитывать нельзя, Martin не отдаёт
// Cache-Control, а токен идёт заголовком, не в URL.
//
// ВЗАИМОДЕЙСТВИЕ ЦЕЛИКОМ У ОСНОВНОЙ КАРТЫ (pointer-events: none на оверлее, interactive: false
// у второго инстанса, синхронизация односторонняя). Панорамирование и зум работают с любой
// половины; двусторонняя синхронизация двух интерактивных карт дала бы двойную инерцию и
// дребезг. Попапы при этом работают в обеих половинах: в стиле основной карты присутствуют оба
// слоя землепользования, и она выбирает источник по позиции клика относительно ручки — см.
// isLanduseNewAtPoint в Map.jsx, позиция уезжает туда через onSplitChange.
// ИМЕННО '!mapbox-gl', как в Map.jsx. Обычный 'mapbox-gl' резолвится в ДРУГОЙ экземпляр
// модуля, и проставленный там mapboxGl.accessToken до него не доезжает — вторая карта
// падала в конструкторе с «An API access token is required to use Mapbox GL».
import mapboxGl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import React, { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { LANDUSE_NEW_LAYER_ID } from '../../../constants/layers';
import * as V from '../../../constants/variables';
import styles from './LanduseCompare.module.css';

const LANDUSE_OLD_LAYER_ID = 'Землепользование';
const LANDUSE_FILL_OPACITY = 0.5;
// styledata у основной карты срабатывает на любое изменение стиля, включая покадровые
// setPaintProperty при проигрывании таймлайна. Копирование стиля дешёвым не назовёшь, поэтому
// склеиваем всплеск в один прогон.
const STYLE_SYNC_DELAY = 200;
const mapLanguage = () => (i18n.locale === 'ru' ? 'ru' : 'en');

export const LanduseCompare = ({ map, mapStyle, transformRequest, active, onSplitChange }) => {
    const containerRef = useRef(null);
    const overlayMapRef = useRef(null);
    // Позиция шторки в пикселях от левого края контейнера. null — ещё не измерили ширину.
    const [ splitX, setSplitX ] = useState(null);
    const draggingRef = useRef(false);

    // Создание второй карты, зеркалирование стиля и односторонняя синхронизация камеры.
    // Зависимостей нет намеренно: карта создаётся один раз на всё время жизни компонента, а он
    // монтируется и размонтируется вместе с включением слоя.
    useEffect(() => {
        // no-op вместо undefined: правило no-undefined, а ветка обязана вернуть функцию
        if (!map || !containerRef.current) return () => {};

        // Стиль основной карты с двумя правками: старого землепользования нет, новое видно.
        // Оба слоя в её стиле уже есть — новый добавлен как additionalLayers у «Землепользования»
        // и держится с fill-opacity 0, пока активен старый источник.
        const buildStyle = () => {
            const style = map.getStyle();
            if (!style) return null;
            return {
                ...style,
                layers: style.layers
                    .filter(({ id }) => id !== LANDUSE_OLD_LAYER_ID)
                    .map(layer => (layer.id === LANDUSE_NEW_LAYER_ID
                        ? {
                            ...layer,
                            layout: { ...layer.layout, visibility: 'visible' },
                            paint: { ...layer.paint, 'fill-opacity': LANDUSE_FILL_OPACITY },
                        }
                        : layer)),
            };
        };
        const overlay = new mapboxGl.Map({
            container: containerRef.current,
            style: buildStyle() ?? V.mapStyles[mapStyle],
            center: map.getCenter(),
            zoom: map.getZoom(),
            bearing: map.getBearing(),
            pitch: map.getPitch(),
            language: i18n.locale,
            attributionControl: false,
            // Вся интерактивность — у основной карты, см. шапку файла.
            interactive: false,
            collectResourceTiming: false,
            transformRequest,
        });
        overlay.setLanguage(mapLanguage());
        overlayMapRef.current = overlay;

        let styleSyncTimer = null;
        const syncStyle = () => {
            clearTimeout(styleSyncTimer);
            styleSyncTimer = setTimeout(() => {
                const overlayMap = overlayMapRef.current;
                const style = buildStyle();
                if (!overlayMap || !style) return;
                // diff по умолчанию: источники у карт совпадают, поэтому обычная правка
                // (включили слой, сменили фильтр) обходится точечными операциями и не роняет
                // уже загруженные тайлы. Смена подложки диффу не поддаётся, и mapbox сам
                // уходит на полную перезагрузку стиля.
                overlayMap.setStyle(style);
                // setStyle перезаписывает стиль, но не язык карты — он свойство инстанса.
                overlayMap.setLanguage(mapLanguage());
            }, STYLE_SYNC_DELAY);
        };
        map.on('styledata', syncStyle);

        // jumpTo, а не easeTo: любое сглаживание на второй карте даёт видимое отставание
        // картинки на стыке при панорамировании.
        const sync = () => {
            if (!overlayMapRef.current) return;
            overlayMapRef.current.jumpTo({
                center: map.getCenter(),
                zoom: map.getZoom(),
                bearing: map.getBearing(),
                pitch: map.getPitch(),
            });
        };
        map.on('move', sync);
        map.on('resize', sync);

        return () => {
            clearTimeout(styleSyncTimer);
            map.off('styledata', syncStyle);
            map.off('move', sync);
            map.off('resize', sync);
            overlay.remove();
            overlayMapRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ map ]);

    // Позиция ручки уезжает наверх, в Map: по ней обработчик клика решает, какой из двух слоёв
    // землепользования показать в попапе — слева старый, справа новый. Пока шторка выключена,
    // правой половины нет, и наверх уходит null.
    useEffect(() => {
        if (!onSplitChange) return () => {};
        onSplitChange(active ? splitX : null);
        return () => onSplitChange(null);
    }, [ active, splitX, onSplitChange ]);

    // Стартовая позиция шторки — середина. Отдельным эффектом, потому что ширину контейнера
    // видно только после монтирования.
    useEffect(() => {
        if (splitX !== null || !containerRef.current) return;
        setSplitX(containerRef.current.getBoundingClientRect().width / 2);
    }, [ splitX ]);

    // ПЕРЕТАСКИВАНИЕ ЧЕРЕЗ POINTER CAPTURE, а не слушателями mousemove на window.
    //
    // Со слушателями на window ручка замирала, когда её вели через открытый попап: попап —
    // обычный DOM-элемент поверх карты, он перехватывает mousemove, а протаскивание курсора по
    // его тексту вдобавок запускает родное выделение, и события до window не доходят. Позиция
    // тогда догоняла только на отпускании кнопки.
    //
    // setPointerCapture адресует ВСЕ последующие pointer-события самой ручке, что бы ни лежало
    // под курсором, поэтому обработчики висят прямо на ней и window не нужен вовсе.
    const onPointerDown = e => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        draggingRef.current = true;
    };
    const onPointerMove = e => {
        if (!draggingRef.current || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
        setSplitX(x);
    };
    const onPointerUp = e => {
        draggingRef.current = false;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
    };
    // Тумблер выключен — срезаем оверлей целиком. Карта при этом продолжает жить и грузить
    // тайлы, ради чего компонент и монтируется вместе со слоем.
    const clipFrom = active
        ? `inset(0 0 0 ${splitX === null ? '50%' : `${splitX}px`})`
        : 'inset(0 0 0 100%)';

    return (
        <>
            <div
                ref={containerRef}
                className={styles.overlay}
                style={{ clipPath: clipFrom }}
            />
            {active && splitX !== null && (
                <div
                    className={styles.handle}
                    style={{ left: `${splitX}px` }}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                >
                    <span className={styles.knob}>
                        <span className={styles.arrowLeft} />
                        <span className={styles.arrowRight} />
                    </span>
                    <span className={`${styles.caption} ${styles.captionLeft}`}>
                        {t`Старый вариант`}
                    </span>
                    <span className={`${styles.caption} ${styles.captionRight}`}>
                        {t`Новый вариант`}
                    </span>
                </div>
            )}
        </>
    );
};
